import { useEffect, useRef, useState } from 'react';

import { MultiSelect, MultiSelectType } from 'components/shared/form/InfiniteScrollDropdown';
import isDeepEqual from 'lib/isDeepEqual';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import { FilterOption } from 'stores/types';

export type FilterMultiSelectProps = Partial<MultiSelectType<FilterOption>> & {
  name: string;
  options?: FilterOption[];
  debounceTime?: number;
  getOptions?: {
    hook: any;
    params?: object;
    optionsToPrepend?: FilterOption[];
  };
};

// Same picks, ignoring order and shape: FilterValueArray re-sorts every commit by name, and some
// parents echo back a reshaped option (the Inbound Referrals list page stores ids and hands back
// `{ id }`) — either way it's still the selection we sent.
type GetOptionValue = (option: FilterOption) => string;

const keyOf = (option: FilterOption, getOptionValue?: GetOptionValue) =>
  String(getOptionValue ? getOptionValue(option) : option.id);

const sameSelection = (a: FilterOption[], b: FilterOption[], getOptionValue?: GetOptionValue) => {
  const keysOf = (options: FilterOption[]) => options.map((option) => keyOf(option, getOptionValue)).sort();
  return isDeepEqual(keysOf(a), keysOf(b));
};

const FilterMultiSelect = (props: FilterMultiSelectProps) => {
  const {
    name,
    options,
    getOptions,
    getOptionLabel,
    getOptionValue,
    placeholder,
    value,
    onChange,
    debounceTime = 0,
  } = props;

  const asyncOptions = options ? { options } : getOptions?.hook(getOptions?.params);

  const [selectedOptions, setSelectedOptions] = useState<FilterOption[]>(value ?? []);
  // The last selection this control and its parent agree on, whichever side set it. Comparing
  // against it (rather than tracking an "edit in flight" flag) is what tells an echo of our own
  // commit apart from a genuinely external change (Clear Filters, the slideout).
  const lastCommittedRef = useRef<FilterOption[]>(value ?? []);
  // The commit runs from a timer, so read the latest onChange rather than the one from the render
  // that scheduled it.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const next = value ?? [];

    if (sameSelection(next, lastCommittedRef.current, getOptionValue)) {
      // An echo of what we already sent. Keep a newer local pick if there is one — resetting to the
      // echo would silently drop it (OLIO-3576: a click landing as the debounce fired got wiped by
      // its predecessor's echo). Otherwise layer the parent's copy over ours: it may carry fresher
      // fields (a renamed building), but a reshaped echo (`{ id }` only) mustn't strip the rest.
      setSelectedOptions((prev) => {
        if (!sameSelection(prev, next, getOptionValue)) return prev;

        const merged = next.map((option) => ({
          ...prev.find((p) => keyOf(p, getOptionValue) === keyOf(option, getOptionValue)),
          ...option,
        }));
        return isDeepEqual(prev, merged) ? prev : merged;
      });
      return;
    }

    lastCommittedRef.current = next;
    setSelectedOptions(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced commit. A timer of our own rather than useDebounce: useDebounce skips a value equal to
  // the last one it settled on, so after an external change, flipping back to the old selection
  // never committed and the parent kept the external one.
  useEffect(() => {
    if (sameSelection(selectedOptions, lastCommittedRef.current, getOptionValue)) return;

    const timeout = setTimeout(() => {
      lastCommittedRef.current = selectedOptions;
      onChangeRef.current?.(new FilterValueArray(selectedOptions));
    }, debounceTime);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, debounceTime]);

  const handleDropdownChange = (changes: FilterValueArray) => {
    setSelectedOptions(changes);
  };

  return (
    <div data-cy={`${name}Filter`}>
      <MultiSelect
        {...asyncOptions}
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        placeholder={placeholder}
        onChange={handleDropdownChange}
        value={selectedOptions}
        prependedOptions={getOptions?.optionsToPrepend || []}
      />
    </div>
  );
};

export default FilterMultiSelect;
